<template>
  <div class="row justify-content-center">
    <!-- Left Column: Login Form -->
    <div class="col-md-5 mb-4 mb-md-0">
      <div class="login-box">
        <h2 class="login-title">{{ $t('auth.login.title') }}</h2>

        <Alert :type="alert.type" :message="alert.message"/>

        <form @submit.prevent="submit" novalidate>
          <div class="mb-3">
            <label class="form-label">{{ $t('auth.login.email') }}</label>
            <input
              type="email"
              class="form-control"
              v-model="email"
              placeholder="example@town.higashikawa.lg.jp"
              :class="{'is-invalid': errors.email}"
              required
            />
            <div v-if="errors.email" class="invalid-feedback d-block">{{ errors.email }}</div>
          </div>

          <div class="mb-3">
            <label class="form-label">{{ $t('auth.login.password') }}</label>
            <div class="password-input-wrapper">
              <input
                :type="showPassword ? 'text' : 'password'"
                class="form-control"
                v-model="password"
                :class="{'is-invalid': errors.password}"
                required
              />
              <button
                type="button"
                class="btn-password-toggle"
                @click="showPassword = !showPassword"
                tabindex="-1"
              >
                <i :class="showPassword ? 'bi bi-eye' : 'bi bi-eye-slash'"></i>
              </button>
            </div>
            <div v-if="errors.password" class="invalid-feedback d-block">{{ errors.password }}</div>
          </div>

          <div class="mb-4">
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="rememberCheckbox" v-model="remember">
              <label class="form-check-label" for="rememberCheckbox">
                {{ $t('auth.login.remember') }}
              </label>
            </div>
          </div>

          <div class="mb-3">
            <button class="btn btn-primary w-100" :disabled="processing">
              <span class="spinner-border spinner-border-sm me-2" v-if="processing"></span>
              <span>{{ $t('auth.login.submit') }}</span>
            </button>
          </div>

          <div class="mt-2">
            <router-link :to="{name: 'forgot-password'}" class="forgot-link">
              {{ $t('auth.login.forgotPasswordLink') }}
              <i class="bi bi-chevron-right ms-1"></i>
            </router-link>
          </div>
        </form>
      </div>
    </div>

    <!-- Right Column: First Time Users -->
    <div class="col-md-5">
      <div class="info-box">
        <h2 class="info-title">{{ $t('auth.login.forNewUserTitle')}}</h2>
        <p class="info-text">
          {{ $t('auth.login.forNewUserDescription')}}
        </p>

        <div>
          <router-link :to="{name: 'signup'}" class="btn btn-primary w-100">{{ $t('auth.login.signupLink') }}</router-link>
          <div class="text-center mt-4">
            &nbsp;
          </div>
        </div>
      </div>
    </div>

    <ErrorDialog
      v-model="showErrorDialogState"
      :message="errorMessage"
      @confirm="closeErrorDialog"
    />
  </div>
</template>

<script setup>
import {ref} from 'vue';
import {useRouter} from 'vue-router';
import {useI18n} from 'vue-i18n';
import {loginUser} from '../../../api/user/AuthApi';
import Alert from '../../../components/Alert.vue';
import ErrorDialog from '../../../components/common/ErrorDialog.vue';

const {t: $t} = useI18n();

const router = useRouter();

const email = ref();
const password = ref();
const remember = ref();
const alert = ref({});
const processing = ref(false);
const errors = ref({});
const showPassword = ref(false);

// Error dialog state
const showErrorDialogState = ref(false);
const errorMessage = ref('');

/**
 * Login
 *
 * @returns {Promise<void>}
 */
const submit = async () => {
  processing.value = true;
  errors.value = {};
  alert.value = {};
  
  // Client-side validation
  if (!validateForm()) {
    processing.value = false;
    return;
  }
  
  try {
    await loginUser(email.value, password.value, remember.value);
    await router.push({name: 'home'});

  } catch (err) {
    if (err.status === 422) {
      // 422 errors indicate invalid credentials
      showErrorDialog($t('auth.login.error.invalid_credentials'));
    } else {
      showErrorDialog($t('auth.login.error.failed'));
      console.error(err);
    }
  }
  processing.value = false;
}

const showErrorDialog = (message) => {
  errorMessage.value = message;
  showErrorDialogState.value = true;
};

const closeErrorDialog = () => {
  showErrorDialogState.value = false;
  errorMessage.value = '';
};

const validateForm = () => {
  let isValid = true;
  errors.value = {};

  // Email address validations
  if (!email.value || email.value.trim() === '') {
    errors.value.email = $t('auth.login.validation.emailRequired');
    isValid = false;
  } else if (!isValidEmail(email.value)) {
    errors.value.email = $t('auth.login.validation.emailInvalid');
    isValid = false;
  }

  // Password validations
  if (!password.value || password.value.trim() === '') {
    errors.value.password = $t('auth.login.validation.passwordRequired');
    isValid = false;
  }

  return isValid;
}

const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

</script>

<style scoped>
.login-box,
.info-box {
  background-color: #FFFFFF;
  padding: 2.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.login-box form {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.info-box {
  justify-content: space-between;
}

.login-title,
.info-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #222222;
  margin-bottom: 1.5rem;
}

.form-label {
  color: #222222;
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.form-control {
  border: 1px solid #D0D0D0;
  border-radius: 4px;
  padding: 0.75rem;
  color: #222222;
}

.form-control:focus {
  border-color: #306FC9;
  box-shadow: 0 0 0 0.2rem rgba(48, 111, 201, 0.25);
}

.form-check-label {
  color: #222222;
  font-size: 0.9rem;
}

.btn-primary {
  background-color: #306FC9;
  border-color: #306FC9;
  color: #FFFFFF;
  padding: 0.75rem;
  font-weight: 500;
  border-radius: 4px;
}

.btn-primary:hover {
  background-color: #2558a8;
  border-color: #2558a8;
}

.btn-primary:disabled {
  background-color: #7a9ed4;
  border-color: #7a9ed4;
}

.signup-link,
.forgot-link {
  color: #306FC9;
  text-decoration: none;
  font-size: 0.9rem;
}

.signup-link:hover,
.forgot-link:hover {
  text-decoration: underline;
}

.info-text {
  color: #222222;
  line-height: 1.8;
  margin-bottom: 2rem;
  flex: 1;
}

.password-input-wrapper {
  position: relative;
}

.password-input-wrapper .form-control {
  padding-right: 2.5rem;
}

.btn-password-toggle {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-password-toggle:hover {
  color: #495057;
}

.btn-password-toggle:focus {
  outline: none;
}

.btn-password-toggle i {
  font-size: 1.1rem;
}
</style>